import axios from 'axios';
import Cookies from 'js-cookie';
import { API_URLS, TIMEOUTS } from '../config/apiConfig';

// Base API configuration for merchant API
const API_BASE_URL = API_URLS.MERCHANT_BASE;

// Create axios instance for Flutter services
const flutterApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: TIMEOUTS.DEFAULT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
flutterApi.interceptors.request.use(
  (config) => {
    const token = Cookies.get('merchant_access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
flutterApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      // Handle authentication errors
      console.error('Flutter services authentication error:', error.response.data);
    }
    return Promise.reject(error);
  }
);

/**
 * Flutter Services API - Handles communication between React app and Flutter app
 * via flutter_inappwebview bridge and backend API calls
 */
class FlutterServicesAPI {
  /**
   * Check if the app is running inside Flutter WebView
   * @returns {boolean}
   */
  static isFlutterApp() {
    try {
      return typeof window !== 'undefined' &&
        window.flutter_inappwebview &&
        typeof window.flutter_inappwebview.callHandler === 'function';
    } catch (error) {
      console.warn('Error checking Flutter app status:', error);
      return false;
    }
  }

  /**
   * Product label for balance / pricing (merchant web + Flutter WebView).
   * @returns {string} Plural unit name shown in UI, e.g. balance headers and cost breakdowns.
   */
  static getCreditLabel() {
    return 'Tokens';
  }

  /**
   * Save FCM token to backend - MOBILE ONLY
   * @param {Object} tokenData - FCM token data
   * @param {string} tokenData.fcm_token - FCM token string
   * @param {string} tokenData.device_type - Device type (android|ios|web)
   * @param {string} tokenData.device_id - Unique device identifier
   * @param {string} tokenData.app_version - App version
   * @returns {Promise<Object>}
   */
  static async saveFCMToken(tokenData) {
    try {
      // Only proceed if in Flutter app (mobile)
      if (!this.isFlutterApp()) {
        console.log("📱 Not in Flutter app, skipping FCM token save");
        return { success: true, message: 'Web environment - no FCM token needed' };
      }

      // Validate input
      if (!tokenData || !tokenData.fcm_token) {
        throw new Error('FCM token is required');
      }

      console.log("📱 Saving FCM token to backend from mobile device:", tokenData);

      // Call Flutter bridge for mobile
      try {
        const flutterToken = await window.flutter_inappwebview.callHandler('saveFCMToken', {
          userToken: tokenData.fcm_token || 'flutter_token'
        });
        console.log("📱 Flutter FCM token response:", flutterToken);
      } catch (flutterError) {
        console.warn('⚠️ Flutter bridge call failed:', flutterError);
        // Continue with backend call even if Flutter fails
      }

      // Save to backend
      const response = await flutterApi.post('/flutter/fcm/save-token', tokenData);
      return response.data;
    } catch (error) {
      console.error('❌ Error saving FCM token:', error);
      throw error;
    }
  }

  /**
   * Logout FCM token (deactivate specific device) - MOBILE ONLY
   * @param {Object} logoutData - Logout data
   * @param {string} logoutData.fcm_token - FCM token to deactivate
   * @returns {Promise<Object>}
   */
  static async logoutFCMToken(logoutData) {
    try {
      // Only proceed if in Flutter app (mobile)
      if (!this.isFlutterApp()) {
        console.log("📱 Not in Flutter app, skipping FCM logout");
        return { success: true, message: 'Web environment - no FCM logout needed' };
      }

      // Validate input
      if (!logoutData || !logoutData.fcm_token) {
        throw new Error('FCM token is required for logout');
      }

      console.log("📱 Logging out FCM token from mobile device:", logoutData);

      // Call Flutter bridge for mobile
      try {
        const flutterResponse = await window.flutter_inappwebview.callHandler('logoutFCMToken', {
          fcmToken: logoutData.fcm_token
        });
        console.log("📱 Flutter logout response:", flutterResponse);
      } catch (flutterError) {
        console.warn('⚠️ Flutter bridge call failed:', flutterError);
        // Continue with backend call even if Flutter fails
      }

      // Deactivate token in backend
      const response = await flutterApi.post('/flutter/fcm/logout', logoutData);
      return response.data;
    } catch (error) {
      console.error('❌ Error logging out FCM token:', error);
      throw error;
    }
  }

  /**
   * Open Razorpay payment in an external browser (Chrome Custom Tab).
   * Flutter bridge opens /razorpay-redirect with the order params, waits for the
   * glammify://razorpay/callback deep link, then resolves with the payment fields.
   *
   * @param {Object} orderData
   * @param {string}  orderData.key_id
   * @param {number}  orderData.amount           — smallest currency unit (paise)
   * @param {string}  orderData.currency
   * @param {boolean} orderData.autopay
   * @param {string} [orderData.rzp_order_id]
   * @param {string} [orderData.razorpay_subscription_id]
   * @param {string}  orderData.description
   * @param {string}  orderData.prefill_name
   * @param {string}  orderData.prefill_email
   * @param {string}  orderData.prefill_contact
   * @param {string}  orderData.redirect_url     — base URL of /razorpay-redirect page
   * @param {string}  orderData.callback_url     — deep link Flutter listens for
   * @returns {Promise<{razorpay_payment_id, razorpay_order_id|razorpay_subscription_id, razorpay_signature}|{failed:true, message:string}>}
   */
  static async openExternalRazorpay(orderData) {
    if (!this.isFlutterApp()) return null;
    return window.flutter_inappwebview.callHandler('openExternalRazorpay', orderData);
  }

  /**
   * Open a URL in the device's external system browser (Chrome on Android, Safari on iOS).
   * Flutter handler: addJavaScriptHandler(handlerName: 'openExternalBrowser',
   *   callback: (args) { launchUrl(Uri.parse(args[0]['url']), mode: LaunchMode.externalApplication); })
   * @param {string} url
   */
  static async openExternalBrowser(url) {
    if (!this.isFlutterApp()) {
      window.open(url, '_blank');
      return;
    }
    return window.flutter_inappwebview.callHandler('openExternalBrowser', { url });
  }

  /**
   * Check file permissions for the current merchant
   * @returns {Promise<Object>}
   */
  static async checkFilePermissions() {
    try {
      console.log("Checking file permissions");

      // Only check with Flutter bridge if in Flutter app
      if (this.isFlutterApp()) {
        try {
          const flutterResponse = await window.flutter_inappwebview.callHandler('checkFilePermission');
          console.log("🔐 Flutter permission response:", flutterResponse);

          // Handle new Flutter response format
          if (flutterResponse && typeof flutterResponse.hasPermission === 'boolean') {
            const hasPermission = flutterResponse.hasPermission;
            const message = flutterResponse.message || 'Permission check completed';

            console.log(`🔐 Permission result: ${hasPermission ? 'GRANTED' : 'DENIED'} - ${message}`);

            return {
              can_upload: hasPermission,
              reason: message,
              permissions: { upload: hasPermission }
            };
          } else {
            // Fallback for old format or invalid response
            console.warn('⚠️ Invalid Flutter response format, treating as denied');
            return {
              can_upload: false,
              reason: 'Invalid permission response',
              permissions: { upload: false }
            };
          }
        } catch (flutterError) {
          console.error('❌ Flutter permission check failed:', flutterError);
          return {
            can_upload: false,
            reason: 'Flutter permission check failed',
            permissions: { upload: false }
          };
        }
      } else {
        console.log("📱 Not in Flutter app, allowing upload");
        return {
          can_upload: true,
          reason: 'Web environment - upload allowed',
          permissions: { upload: true }
        };
      }
    } catch (error) {
      console.error('Error checking file permissions:', error);
      throw error;
    }
  }

  /**
   * Get device information - MOBILE ONLY for FCM, Web-safe fallback
   * @returns {Promise<Object>}
   */
  static async getDeviceInfo() {
    try {
      console.log("📱 Getting device info");

      // Only get from Flutter if available (mobile)
      if (this.isFlutterApp()) {
        try {
          const flutterDeviceInfo = await window.flutter_inappwebview.callHandler('getDeviceInfo');
          console.log("📱 Flutter device info received:", flutterDeviceInfo);

          // Log FCM token specifically
          if (flutterDeviceInfo && flutterDeviceInfo.fcmToken) {
            console.log("🔥 FCM Token from Flutter:", flutterDeviceInfo.fcmToken);

            // Save FCM token to backend for logged-in user (mobile only)
            try {
              console.log("💾 Saving FCM token to backend...");
              const tokenData = {
                fcm_token: flutterDeviceInfo.fcmToken,
                device_type: flutterDeviceInfo.platform || 'mobile',
                device_id: flutterDeviceInfo.deviceId || 'unknown',
                app_version: flutterDeviceInfo.version || '1.0.0'
              };

              const saveResult = await this.saveFCMToken(tokenData);
              console.log("✅ FCM token saved successfully:", saveResult);
            } catch (saveError) {
              console.error("❌ Failed to save FCM token:", saveError);
            }
          } else {
            console.log("⚠️ No FCM token found in device info");
          }

          // Return the Flutter device info
          return flutterDeviceInfo;
        } catch (flutterError) {
          console.error('❌ Flutter getDeviceInfo failed:', flutterError);
          throw flutterError;
        }
      } else {
        // Web environment - return safe defaults without FCM functionality
        console.log("🌐 Web environment detected, returning web-safe device info");
        return {
          platform: 'web',
          version: 'web',
          fcmToken: null,
          deviceId: 'web-device',
          isWeb: true
        };
      }
    } catch (error) {
      console.error('❌ Error getting device info:', error);
      throw error;
    }
  }

  /**
   * Handle incoming notification when app is in foreground
   * @param {Object} notification - Notification data from FCM
   * @returns {Promise<Object>}
   */
  static async handleIncomingNotification(notification) {
    try {
      console.log("📱 Handling incoming notification:", notification);

      // Call Flutter bridge to handle notification if in Flutter app
      if (this.isFlutterApp()) {
        try {
          const flutterResponse = await window.flutter_inappwebview.callHandler('handleIncomingNotification', notification);
          console.log("📱 Flutter notification response:", flutterResponse);
          return flutterResponse;
        } catch (flutterError) {
          console.warn('⚠️ Flutter notification handling failed:', flutterError);
          // Fallback to web notification handling
        }
      }

      // Web notification handling
      if ('Notification' in window && Notification.permission === 'granted') {
        const webNotification = new Notification(notification.title, {
          body: notification.body,
          icon: notification.image || '/favicon.ico',
          data: notification.data || {}
        });

        // Handle notification click
        webNotification.onclick = function () {
          console.log("📱 Web notification clicked:", notification);
          // Handle notification click based on data
          if (notification.data && notification.data.action) {
            // Navigate or perform action based on notification data
            console.log("📱 Performing notification action:", notification.data.action);
          }
        };

        return { success: true, handled: 'web' };
      }

      return { success: false, message: 'No notification handling available' };
    } catch (error) {
      console.error('Error handling incoming notification:', error);
      throw error;
    }
  }

  /**
   * Subscribe to notification topics
   * @param {Array<string>} topics - Array of topic names to subscribe to
   * @returns {Promise<Object>}
   */
  static async subscribeToTopics(topics) {
    try {
      console.log("📱 Subscribing to topics:", topics);

      if (!Array.isArray(topics) || topics.length === 0) {
        throw new Error('Topics array is required');
      }

      // Call Flutter bridge to subscribe to topics if in Flutter app
      if (this.isFlutterApp()) {
        try {
          const flutterResponse = await window.flutter_inappwebview.callHandler('subscribeToTopics', topics);
          console.log("📱 Flutter topic subscription response:", flutterResponse);

          // Also subscribe via backend API
          const backendResponse = await flutterApi.post('/notifications/subscribe-topic', {
            topic: topics[0], // Backend handles one topic at a time
            merchant_ids: [] // Current merchant will be used
          });
          console.log("📱 Backend topic subscription response:", backendResponse.data);

          return {
            success: true,
            flutter: flutterResponse,
            backend: backendResponse.data
          };
        } catch (flutterError) {
          console.warn('⚠️ Flutter topic subscription failed:', flutterError);
          // Try backend only
          const backendResponse = await flutterApi.post('/notifications/subscribe-topic', {
            topic: topics[0],
            merchant_ids: []
          });
          return {
            success: true,
            flutter: null,
            backend: backendResponse.data
          };
        }
      } else {
        console.log("📱 Not in Flutter app, subscribing via backend only");
        const backendResponse = await flutterApi.post('/notifications/subscribe-topic', {
          topic: topics[0],
          merchant_ids: []
        });
        return {
          success: true,
          flutter: null,
          backend: backendResponse.data
        };
      }
    } catch (error) {
      console.error('Error subscribing to topics:', error);
      throw error;
    }
  }

  /**
   * Unsubscribe from notification topics
   * @param {Array<string>} topics - Array of topic names to unsubscribe from
   * @returns {Promise<Object>}
   */
  static async unsubscribeFromTopics(topics) {
    try {
      console.log("📱 Unsubscribing from topics:", topics);

      if (!Array.isArray(topics) || topics.length === 0) {
        throw new Error('Topics array is required');
      }

      // Call Flutter bridge to unsubscribe from topics if in Flutter app
      if (this.isFlutterApp()) {
        try {
          const flutterResponse = await window.flutter_inappwebview.callHandler('unsubscribeFromTopics', topics);
          console.log("📱 Flutter topic unsubscription response:", flutterResponse);

          // Also unsubscribe via backend API
          const backendResponse = await flutterApi.post('/notifications/unsubscribe-topic', {
            topic: topics[0], // Backend handles one topic at a time
            merchant_ids: [] // Current merchant will be used
          });
          console.log("📱 Backend topic unsubscription response:", backendResponse.data);

          return {
            success: true,
            flutter: flutterResponse,
            backend: backendResponse.data
          };
        } catch (flutterError) {
          console.warn('⚠️ Flutter topic unsubscription failed:', flutterError);
          // Try backend only
          const backendResponse = await flutterApi.post('/notifications/unsubscribe-topic', {
            topic: topics[0],
            merchant_ids: []
          });
          return {
            success: true,
            flutter: null,
            backend: backendResponse.data
          };
        }
      } else {
        console.log("📱 Not in Flutter app, unsubscribing via backend only");
        const backendResponse = await flutterApi.post('/notifications/unsubscribe-topic', {
          topic: topics[0],
          merchant_ids: []
        });
        return {
          success: true,
          flutter: null,
          backend: backendResponse.data
        };
      }
    } catch (error) {
      console.error('Error unsubscribing from topics:', error);
      throw error;
    }
  }

  /**
   * Send notification to a single user (for AI generation completion)
   * @param {Object} notificationData - Notification data
   * @param {string} notificationData.merchant_id - Target merchant ID
   * @param {string} notificationData.title - Notification title
   * @param {string} notificationData.body - Notification body
   * @param {Object} notificationData.data - Additional data (optional)
   * @param {string} notificationData.image_url - Image URL (optional)
   * @returns {Promise<Object>}
   */
  static async sendSingleNotification(notificationData) {
    try {
      console.log("📱 Sending single notification:", notificationData);

      // Validate required fields
      if (!notificationData.merchant_id || !notificationData.title || !notificationData.body) {
        throw new Error('merchant_id, title, and body are required');
      }

      const response = await flutterApi.post('/notifications/send-single', notificationData);
      console.log("📱 Single notification sent successfully:", response.data);

      return response.data;
    } catch (error) {
      console.error('Error sending single notification:', error);
      // Return a structured error response instead of throwing
      return {
        success: false,
        message: error.message || 'Failed to send notification',
        error: error
      };
    }
  }

  /**
   * Send announcement to all users
   * @param {Object} announcementData - Announcement data
   * @param {string} announcementData.title - Announcement title
   * @param {string} announcementData.body - Announcement body
   * @param {Object} announcementData.data - Additional data (optional)
   * @param {string} announcementData.image_url - Image URL (optional)
   * @returns {Promise<Object>}
   */
  static async sendAnnouncement(announcementData) {
    try {
      console.log("📱 Sending announcement:", announcementData);

      // Validate required fields
      if (!announcementData.title || !announcementData.body) {
        throw new Error('title and body are required');
      }

      const response = await flutterApi.post('/notifications/send-announcement', announcementData);
      console.log("📱 Announcement sent successfully:", response.data);

      return response.data;
    } catch (error) {
      console.error('Error sending announcement:', error);
      throw error;
    }
  }

  /**
   * Send notification to topic subscribers
   * @param {Object} topicData - Topic notification data
   * @param {string} topicData.topic - Topic name
   * @param {string} topicData.title - Notification title
   * @param {string} topicData.body - Notification body
   * @param {Object} topicData.data - Additional data (optional)
   * @param {string} topicData.image_url - Image URL (optional)
   * @returns {Promise<Object>}
   */
  static async sendTopicNotification(topicData) {
    try {
      console.log("📱 Sending topic notification:", topicData);

      // Validate required fields
      if (!topicData.topic || !topicData.title || !topicData.body) {
        throw new Error('topic, title, and body are required');
      }

      const response = await flutterApi.post('/notifications/send-topic', topicData);
      console.log("📱 Topic notification sent successfully:", response.data);

      return response.data;
    } catch (error) {
      console.error('Error sending topic notification:', error);
      throw error;
    }
  }

  /**
   * Request notification permissions
   * @returns {Promise<Object>}
   */
  static async requestNotificationPermissions() {
    try {
      console.log("📱 Requesting notification permissions");

      // Call Flutter bridge to request permissions if in Flutter app
      if (this.isFlutterApp()) {
        try {
          const flutterResponse = await window.flutter_inappwebview.callHandler('requestNotificationPermissions');
          console.log("📱 Flutter permission response:", flutterResponse);
          return flutterResponse;
        } catch (flutterError) {
          console.warn('⚠️ Flutter permission request failed:', flutterError);
          // Fallback to web notification permission
        }
      }

      // Web notification permission request
      if ('Notification' in window) {
        const permission = await Notification.requestPermission();
        console.log("📱 Web notification permission:", permission);
        return {
          success: permission === 'granted',
          permission: permission,
          platform: 'web'
        };
      }

      return {
        success: false,
        message: 'Notifications not supported',
        platform: 'unknown'
      };
    } catch (error) {
      console.error('Error requesting notification permissions:', error);
      throw error;
    }
  }

  /**
   * Check notification permission status
   * @returns {Promise<Object>}
   */
  static async checkNotificationPermissions() {
    try {
      console.log("📱 Checking notification permissions");

      // Call Flutter bridge to check permissions if in Flutter app
      if (this.isFlutterApp()) {
        try {
          const flutterResponse = await window.flutter_inappwebview.callHandler('checkNotificationPermissions');
          console.log("📱 Flutter permission check response:", flutterResponse);
          return flutterResponse;
        } catch (flutterError) {
          console.warn('⚠️ Flutter permission check failed:', flutterError);
          // Fallback to web notification permission check
        }
      }

      // Web notification permission check
      if ('Notification' in window) {
        const permission = Notification.permission;
        console.log("📱 Web notification permission status:", permission);
        return {
          granted: permission === 'granted',
          permission: permission,
          platform: 'web'
        };
      }

      return {
        granted: false,
        permission: 'unsupported',
        platform: 'unknown'
      };
    } catch (error) {
      console.error('Error checking notification permissions:', error);
      throw error;
    }
  }

  /**
   * Download file(s) to device storage (mobile only)
   * Supports both single file and multiple files formats
   * 
   * Single file format (legacy):
   * @param {string} downloadData.fileName - File name for download
   * @param {string} downloadData.fileExtension - File extension (mp4, jpg, etc.)
   * @param {string} downloadData.downloadUrl - URL to download from
   * @param {string} downloadData.accessToken - Access token for authentication
   * @param {string} downloadData.method - HTTP method (default: GET)
   * 
   * Multiple files format (new):
   * @param {string} downloadData.accessToken - Access token for authentication
   * @param {string} downloadData.method - HTTP method (default: GET)
   * @param {Array<Object>} downloadData.files - Array of file objects
   * @param {string} downloadData.files[].fileName - File name
   * @param {string} downloadData.files[].fileExtension - File extension
   * @param {string} downloadData.files[].downloadUrl - Download URL
   * 
   * @returns {Promise<Object>}
   */
  static async downloadFileToStorage(downloadData) {
    try {
      console.log("📱 Starting mobile download:", downloadData);

      // Only proceed if in Flutter app
      if (!this.isFlutterApp()) {
        console.log("📱 Not in Flutter app, skipping mobile download");
        return {
          success: false,
          message: 'Mobile download only available in Flutter app',
          platform: 'web'
        };
      }

      // Detect if this is a multiple files download (new format) or single file (legacy)
      const isMultipleFiles = downloadData.files && Array.isArray(downloadData.files) && downloadData.files.length > 0;

      if (isMultipleFiles) {
        console.log(`📱 Multiple files download detected: ${downloadData.files.length} files`);
      } else {
        console.log("📱 Single file download detected (legacy format)");

        // Validate single file input
        if (!downloadData || !downloadData.generationId || !downloadData.fileType) {
          throw new Error('generationId and fileType are required for single file download');
        }
      }

      // Step 1: Check file permissions before download
      console.log("🔐 Checking file permissions for download...");
      const permissionResult = await this.checkFilePermissions();

      if (!permissionResult.can_upload) {
        console.warn("❌ File permission denied:", permissionResult.reason);
        return {
          success: false,
          message: `Permission denied: ${permissionResult.reason}`,
          permission: false,
          platform: 'mobile'
        };
      }

      console.log("✅ File permission granted, proceeding with download");

      // Step 2: Call Flutter bridge to download file(s) to storage
      try {
        let flutterRequestData;

        if (isMultipleFiles) {
          // New format: Multiple files with files array
          flutterRequestData = {
            accessToken: downloadData.accessToken || '',
            method: downloadData.method || 'GET',
            files: downloadData.files.map(file => ({
              fileName: file.fileName,
              fileExtension: file.fileExtension,
              downloadUrl: file.downloadUrl,
              generationId: file.generationId || '', // Optional
              fileType: file.fileType || 'IMAGE' // Optional
            }))
          };

          console.log(`📱 ========== MULTIPLE FILES DOWNLOAD DEBUG ==========`);
          console.log(`📱 Number of files: ${flutterRequestData.files.length}`);
          console.log(`📱 Access token present: ${!!flutterRequestData.accessToken}`);
          console.log(`📱 Method: ${flutterRequestData.method}`);
          console.log(`📱 Files array:`, JSON.stringify(flutterRequestData.files, null, 2));
          console.log(`📱 Full request data:`, JSON.stringify(flutterRequestData, null, 2));
          console.log(`📱 Flutter should see this as args[0]['files'] with ${flutterRequestData.files.length} items`);
          console.log(`📱 ==================================================`);
        } else {
          // Legacy format: Single file download
          flutterRequestData = {
            generationId: downloadData.generationId,
            fileType: downloadData.fileType,
            fileName: downloadData.fileName || `glammify-${downloadData.generationId}`,
            downloadUrl: downloadData.downloadUrl,
            fileExtension: downloadData.fileExtension || (downloadData.fileType === 'VIDEO' ? 'mp4' : 'jpg'),
            method: downloadData.method || (downloadData.fileType === 'ZIP' ? 'GET' : 'POST'),
            headers: downloadData.headers || {},
            accessToken: downloadData.accessToken || '',
            selectedItems: downloadData.selectedItems || [],
            id: downloadData.id || downloadData.generationId,
            ...downloadData // Pass through all additional parameters
          };

          console.log("📱 Calling Flutter bridge with data (legacy format):", flutterRequestData);

          // Validate download URL for single file
          if (!downloadData.downloadUrl) {
            throw new Error('Download URL is required');
          }
        }

        // Add timeout to prevent hanging
        const timeoutDuration = isMultipleFiles ? 30000 : 15000; // Longer timeout for multiple files
        const flutterResponse = await Promise.race([
          window.flutter_inappwebview.callHandler('downloadFileToStorage', flutterRequestData),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Flutter bridge timeout')), timeoutDuration)
          )
        ]);

        console.log("📱 Flutter download response:", flutterResponse);

        // Handle Flutter response - be more lenient with response validation
        if (flutterResponse) {
          if (flutterResponse.success === true) {
            const fileCount = isMultipleFiles ? downloadData.files.length : 1;
            return {
              success: true,
              message: isMultipleFiles
                ? `${fileCount} files downloaded successfully to device storage`
                : 'File downloaded successfully to device storage',
              filePath: flutterResponse.filePath,
              platform: 'mobile',
              flutter: flutterResponse
            };
          } else if (flutterResponse.success === false) {
            return {
              success: false,
              message: flutterResponse.message || 'Download failed',
              platform: 'mobile',
              flutter: flutterResponse
            };
          } else {
            // Flutter bridge returned something but no clear success/failure
            // Assume success if no explicit failure
            console.log("📱 Ambiguous Flutter response, assuming success:", flutterResponse);
            return {
              success: true,
              message: 'File download initiated successfully',
              platform: 'mobile',
              flutter: flutterResponse
            };
          }
        } else {
          // No response from Flutter bridge - assume success
          // Many Flutter bridges don't return responses but still work
          console.log("📱 No Flutter response, assuming download initiated");
          return {
            success: true,
            message: 'File download initiated successfully',
            platform: 'mobile',
            flutter: null
          };
        }
      } catch (flutterError) {
        console.error('❌ Flutter download failed:', flutterError);
        return {
          success: false,
          message: `Flutter download error: ${flutterError.message}`,
          platform: 'mobile',
          error: flutterError
        };
      }
    } catch (error) {
      console.error('Error in downloadFileToStorage:', error);
      return {
        success: false,
        message: error.message || 'Download failed',
        platform: 'mobile',
        error: error
      };
    }
  }

  /**
   * Download gallery PDF catalogue.
   * - In Flutter app: trigger native storage download through bridge using POST payload.
   * - In web: return backend blob response so caller can save with browser download.
   * @param {Array<number>} generationIds - Selected generation IDs
   * @returns {Promise<Object|import('axios').AxiosResponse<Blob>>}
   */
  static async downloadGalleryPdfCatalogue(generationIds = [], brandLogoId = null) {
    try {
      if (!Array.isArray(generationIds) || generationIds.length === 0) {
        throw new Error('At least one generation ID is required');
      }

      // Keep a single endpoint contract for Flutter + web callers.
      const endpointPath = '/ai-generation/gallery/download-pdf';

      if (this.isFlutterApp()) {
        const accessToken = Cookies.get('merchant_access_token');
        if (!accessToken) {
          throw new Error('Access token not found. Please login again.');
        }

        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        return await this.downloadFileToStorage({
          generationId: `catalog-${timestamp}`,
          fileType: 'PDF',
          fileName: `glammify_catalogue_${timestamp}`,
          fileExtension: 'pdf',
          downloadUrl: `${API_URLS.BACKEND_BASE}/api/merchant/ai-generation/gallery/download-pdf`,
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          },
          accessToken,
          // Send selected items explicitly for Flutter handlers that consume this field.
          selectedItems: generationIds,
          body: { generation_ids: generationIds, brand_logo_id: brandLogoId },
          generation_ids: generationIds,
          brand_logo_id: brandLogoId
        });
      }

      return await flutterApi.post(
        endpointPath,
        { generation_ids: generationIds, brand_logo_id: brandLogoId },
        { responseType: 'blob' }
      );
    } catch (error) {
      console.error('❌ Error downloading PDF catalogue:', error);
      throw error;
    }
  }

  /**
   * Payload for share_whatsapp_catalog / share_mail_catalog bridge handlers.
   * @param {Object} catalog - Catalog row from merchant API
   * @param {string} shareUrl - Public viewer URL (/catalog/:token)
   */
  static buildCatalogSharePayload(catalog, shareUrl) {
    const catalogName = (catalog?.name || '').trim();
    const description = (catalog?.description || '').trim();

    // Email subject — generic label (no “Share”, no catalog name)
    const subject = 'Glammify Catalogue';

    // socialShare title — short headline like Gallery shareTitle
    const title = 'My jewellery catalogue!';

    // Body — gallery headline + link line (no emoji: Flutter bridge corrupts surrogate-pair emoji in WhatsApp deep links)
    let shareMessage = catalogName
      ? `Jewellery catalogue "${catalogName}" — created with Glammify\n\nView the full collection here: ${shareUrl}`
      : `Jewellery catalogue — created with Glammify\n\nView the full collection here: ${shareUrl}`;

    if (description) {
      shareMessage += `\n\n${description}`;
    }

    return {
      catalogId: catalog?.id ?? null,
      catalogName: catalogName || null,
      shareMessage,
      subject,
      title,
      text: shareMessage,
      message: shareMessage,
    };
  }

  /**
   * Native share sheet for catalog (existing Flutter handler: socialShare).
   * @returns {Promise<Object>}
   */
  static async shareCatalogSocial(catalog, shareUrl) {
    if (!this.isFlutterApp()) {
      throw new Error('socialShare is only available in the Flutter app');
    }
    if (!shareUrl) {
      throw new Error('Share URL is required');
    }
    const payload = this.buildCatalogSharePayload(catalog, shareUrl);
    return window.flutter_inappwebview.callHandler('socialShare', payload);
  }

  /**
   * Open WhatsApp with catalog share text (Flutter handler: share_whatsapp_catalog).
   * @returns {Promise<Object>}
   */
  static async shareWhatsappCatalog(catalog, shareUrl) {
    if (!this.isFlutterApp()) {
      throw new Error('share_whatsapp_catalog is only available in the Flutter app');
    }
    if (!shareUrl) {
      throw new Error('Share URL is required');
    }
    const payload = this.buildCatalogSharePayload(catalog, shareUrl);
    return window.flutter_inappwebview.callHandler('share_whatsapp_catalog', payload);
  }

  /**
   * Open mail client with catalog share (Flutter handler: share_mail_catalog).
   * @returns {Promise<Object>}
   */
  static async shareMailCatalog(catalog, shareUrl) {
    if (!this.isFlutterApp()) {
      throw new Error('share_mail_catalog is only available in the Flutter app');
    }
    if (!shareUrl) {
      throw new Error('Share URL is required');
    }
    const payload = this.buildCatalogSharePayload(catalog, shareUrl);
    return window.flutter_inappwebview.callHandler('share_mail_catalog', payload);
  }

  /**
   * Download catalog as PDF.
   * - Flutter app: triggers native storage download via downloadFileToStorage bridge (GET, no auth needed).
   * - Web: returns the PDF download URL for the caller to trigger a browser download.
   * @param {string} token - Active share link token
   * @param {string} catalogName - Catalog name for the filename
   * @returns {Promise<Object>}
   */
  static async downloadCatalogPdf(token, catalogName = '') {
    try {
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const safeName = (catalogName || 'catalogue').replace(/[^a-zA-Z0-9_-]/g, '_');
      const fileName = `glammify_${safeName}_${timestamp}`;
      const downloadUrl = `${API_URLS.BACKEND_BASE}/api/public/catalog/${token}/pdf`;

      if (this.isFlutterApp()) {
        return await this.downloadFileToStorage({
          generationId: `catalog-pdf-${timestamp}`,
          fileType: 'PDF',
          fileName,
          fileExtension: 'pdf',
          downloadUrl,
          method: 'GET',
          headers: {},
          accessToken: '',
        });
      }

      // Web fallback — fetch as blob so Chrome doesn't block a cross-origin HTTP download
      const response = await fetch(downloadUrl);
      if (!response.ok) {
        throw new Error(`PDF fetch failed: ${response.status} ${response.statusText}`);
      }
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      return { success: true, platform: 'web', blobUrl, fileName: `${fileName}.pdf` };
    } catch (error) {
      console.error('❌ Error downloading catalog PDF:', error);
      throw error;
    }
  }
}

export default FlutterServicesAPI;